import React, { useMemo, useState, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { getValueFromResource } from "../../common/timeLiveFunction";
import { addDashboardWidgetAsync, updateRefreshState } from "./dashboardSlice";
import { t } from "i18next";
import { ShowToast } from "../../common/Toastr";

interface Widget {
  SystemDashboardTemplateName: string;
  SystemWidgetImagePath: string | null;
  SystemDashboardWidgetName: string;
  SystemDashboardWidgetDescription: string;
  SystemDashboardWidgetId: number;
  IsFeatureEnabled: boolean;
}

interface Props {
  show?: boolean;
  onClose: () => void;
}

const bgColors = [
  "#8688fc", "#f5893d", "#56b8ac", "#e273ab", "#608dd4", "#ae77e2", "#98b864", "#ee5d5d"
];

const overlayBaseStyles: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  zIndex: 5,
  borderRadius: "12px",
  background: "rgba(15, 23, 42, 0.88)",
  backdropFilter: "blur(6px)",
  WebkitBackdropFilter: "blur(6px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "20px",
  transition: "opacity 0.22s ease",
};

const AddWidgetsModal = ({ show, onClose }: Props) => {
  const { systemWidgets, EmployeeDashboardId, dashboardWidgetDetails } = useAppSelector((state) => state.dashboard);
  const widgets: Widget[] = systemWidgets || [];
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const dispatch = useAppDispatch();
  const [selectedWidget, setSelectedWidget] = useState<any>(null);
  const modalBodyRef = useRef<HTMLDivElement | null>(null);
  const firstMatchRef = useRef<HTMLDivElement | null>(null);
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);

  const groupedWidgets = useMemo(() => {
    const groups: Record<string, Widget[]> = {};
    widgets.forEach((widget) => {
      const searchText = search.toLowerCase();
      const isMatch =
        widget.SystemDashboardWidgetName?.toLowerCase().includes(searchText) ||
        widget.SystemDashboardWidgetDescription?.toLowerCase().includes(searchText);

      if (!isMatch) return;

      const key = widget.SystemDashboardTemplateName || "Default";
      if (!groups[key]) groups[key] = [];
      groups[key].push(widget);
    });
    return groups;
  }, [widgets, search]);

  const categories = useMemo(() => ["All", ...Object.keys(groupedWidgets)], [groupedWidgets]);

  const getImagePath = (path: string | null) => {
    return path ? `/media/svg/WidgetsSVG/${path}` : "/media/svg/WidgetsSVG/Donut-chart1.svg";
  };

  const renderCards = (charts: Widget[], templateName: any) => {
    return charts.map((chart, index) => {
      const isFirst = index === 0;
      const allowed = chart.IsFeatureEnabled !== false;
      const cardKey = `${templateName}-${index}`;
      const isHovered = hoveredCard === cardKey;
      return (
        <div
          className="col-lg-4 col-md-6 col-sm-12 mb-4"
          key={index}
          ref={isFirst ? firstMatchRef : null}
        >
          <div
            className="card cursor-pointer h-100"
            onClick={() => {
              if (!allowed) return;
              setPreviewImage(getImagePath(chart.SystemWidgetImagePath));
              setSelectedWidget(chart);
            }}
            onMouseEnter={() => setHoveredCard(cardKey)}
            onMouseLeave={() => setHoveredCard(null)}
            style={{
              borderRadius: "12px",
              // border: "2px solid #f3f4f6",
              border: `2px solid ${allowed ? "#f3f4f6" : "#e5e7eb"}`,
              boxShadow: "0 2px 4px rgba(0,0,0,0.03)",
              padding: "20px",
              display: "flex",
              flexDirection: "column",
              transition: "all 0.2s ease-in-out",
              background: "#fff",
              // background: allowed ? "#fff" : "#f3f4f6",
              cursor: allowed ? "pointer" : "not-allowed",
              // opacity: allowed ? 1 : 0.6,
              position: "relative",
              overflow: "hidden",
            }}
            onMouseOver={(e) => {
              if (!allowed) return;
              e.currentTarget.style.boxShadow = "0 8px 16px rgba(0,0,0,0.08)";
              e.currentTarget.style.transform = "translateY(-3px)";
              e.currentTarget.style.borderColor = "#e5e7eb";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.03)";
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.borderColor = "#f3f4f6";
            }}
          >
            {!allowed && (
              <div
                style={{
                  position: "absolute",
                  ...overlayBaseStyles,
                  inset: 0,
                  zIndex: 5,
                  borderRadius: "12px",
                  background: "rgba(17, 24, 39, 0.9)",
                  backdropFilter: "blur(16px)",
                  opacity: isHovered ? 1 : 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  pointerEvents: isHovered ? "auto" : "none",
                  padding: "16px",
                }}
              >
                <div
                  style={{
                    textAlign: "center",
                    color: "#fff",
                    maxWidth: "220px",
                  }}
                >

                  <div
                    style={{
                      width: "44px",
                      height: "44px",
                      borderRadius: "12px",
                      // background: "rgba(17, 24, 39, 0.9)",
                      background: "rgba(255,255,255,0.12)",
                      border: "1px solid rgba(255,255,255,0.18)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 10px",
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M5 7V5a3 3 0 0 1 6 0v2"
                        stroke="#fff"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                      />
                      <rect
                        x="3"
                        y="7"
                        width="10"
                        height="8"
                        rx="2"
                        stroke="#fff"
                        strokeWidth="1.6"
                      />
                    </svg>
                  </div>

                  <div
                    style={{
                      fontSize: "11.5px",
                      lineHeight: "1.4",
                      marginBottom: "12px",
                      fontWeight: 500,
                      color: "rgba(255,255,255,0.9)",
                    }}
                  >
                     {getValueFromResource(`${templateName} is not enabled in your Subscription. Contact your administrator or sales@livetecs.com`, dispatch, t)}
                  </div>
                  {/* <button
                    className="btn btn-primary btn-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open("https://livetecs.com/timelive/pricing/", "_blank");
                    }}

                  >
                    Upgrade
                  </button> */}
                </div>
              </div>
            )}

            <div
              style={{
                backgroundColor: bgColors[index % bgColors.length],
                border: "10px solid rgba(0,0,0,0.04)",
                borderRadius: "8px",
                height: "130px",
                width: "100%",
                overflow: "hidden",
                marginBottom: "16px",
                borderBottom: "0px",
                filter: "none",
              }}
            >
              <img
                src={getImagePath(chart.SystemWidgetImagePath)}
                alt={chart.SystemDashboardWidgetName}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  objectPosition: "top center",
                  opacity: 1,
                }}
              />
            </div>

            <div className="text-start mt-auto">
              <h6
                className="fw-bold mb-1 text-truncate"
                style={{ fontSize: "15px", color: allowed ? "#1f2937" : "#9ca3af" }}
              >
                {/* {chart.SystemDashboardWidgetName} */}
                {getValueFromResource(`${chart.SystemDashboardWidgetName}`, dispatch, t)}
              </h6>
            </div>
          </div>
        </div>
      );
    });
  };

  const handleAddWidget = () => {
    if (!selectedWidget) return;
    // if (dashboardWidgetDetails?.length >= 8) {
    //   ShowToast("Warning", 'You can not add more than 8 widgets in one dashboard.', "warning", "toast-top-right");
    //   return;
    // }
    const payload = {
      AccountEmployeeDashboardsId: EmployeeDashboardId,
      SystemDashboardWidgetId: selectedWidget.SystemDashboardWidgetId
    };
    dispatch(addDashboardWidgetAsync(payload))
      .unwrap()
      .then((res: any) => {
        if (res.StatusCode === "200") {
          dispatch(updateRefreshState(true));
          ShowToast(res.ResponseMessage, "Success", "success", "toast-top-right");
          onClose();
        } else {
          dispatch(updateRefreshState(true));
          ShowToast(res.ResponseMessage, "Warning", "warning", "toast-top-right");
          onClose();
        }
      });
  };

  return (
    <>
      <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 1050 }}>
        <div className="modal-dialog modal-xl modal-dialog-centered">
          <div className="modal-content" style={{ borderRadius: "14px", height: "85vh", overflow: "hidden", border: "none" }}>

            <div className="modal-header d-flex align-items-center justify-content-between p-4 border-bottom bg-white">
              <h4 className="modal-title fw-bold m-0 text-dark">{getValueFromResource('Add Widget', dispatch, t)}</h4>
              <div className="d-flex align-items-center gap-3">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={getValueFromResource('Search', dispatch, t)}
                  className="form-control h-35px w-250px fs-7"
                />
                <button className="btn-close" onClick={onClose}></button>
              </div>
            </div>

            <div className="modal-body p-0 d-flex" style={{ height: "calc(85vh - 70px)" }}>

              <div className="bg-light border-end" style={{ width: "240px", minWidth: "240px", overflowY: "auto", padding: "15px 10px" }}>
                <div className="d-flex flex-column gap-1">
                  {categories?.map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setSelectedCategory(cat)}
                      className="btn text-start border-0 py-3 px-4 w-100"
                      style={{
                        borderRadius: "10px",
                        fontSize: "13.5px",
                        fontWeight: selectedCategory === cat ? "700" : "500",
                        transition: "0.2s all",
                        backgroundColor: selectedCategory === cat ? "#fff" : "transparent",
                        color: selectedCategory === cat ? "#009ef7" : "#5e6278",
                        boxShadow: selectedCategory === cat ? "0 4px 10px rgba(0,0,0,0.05)" : "none",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between"
                      }}
                    >
                      {/* {cat} */}
                      {getValueFromResource(`${cat}`, dispatch, t)}
                      {selectedCategory === cat && <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#009ef7" }}></span>}
                    </button>
                  ))}
                </div>
              </div>

              <div ref={modalBodyRef} className="flex-grow-1 p-8 custom-scrollbar" style={{ overflowY: "auto", backgroundColor: "#fff" }}>
                {Object.entries(groupedWidgets)?.map(([templateName, widgets]) => {
                  if (selectedCategory !== "All" && selectedCategory !== templateName) return null;
                  return (
                    <div key={templateName} className="mb-6">
                      <div className="d-flex align-items-center gap-2 mb-4">
                        <h4 className="fw-bold m-0 text-dark" style={{ fontSize: "17px" }}> {getValueFromResource(`${templateName}`, dispatch, t)}</h4>
                        <span className="badge badge-light-primary rounded-pill px-3 py-1 fs-9">{widgets.length} Widgets</span>
                      </div>
                      <div className="row g-4">{renderCards(widgets, templateName)}</div>
                    </div>
                  );
                })}
              </div>

            </div>
          </div>
        </div>
      </div>

      {previewImage && (
        <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.7)", zIndex: 2000 }}>
          <div className="modal-dialog modal-md modal-dialog-centered">
            <div className="modal-content shadow-lg" style={{ borderRadius: "12px" }}>
              <div className="modal-header">
                <h5 className="fw-bold m-0">{getValueFromResource(`${selectedWidget.SystemDashboardWidgetName}`, dispatch, t)}</h5>
                <button className="btn-close" onClick={() => setPreviewImage(null)}></button>
              </div>
              <div className="modal-body text-center p-6">
                <img src={previewImage} alt="preview" style={{ width: "100%", maxHeight: "50vh", objectFit: "contain", borderRadius: "10px" }} />
                <p
                  className="mb-0"
                  style={{
                    fontSize: "13px",
                    color: "#6b7280",
                    lineHeight: "1.4",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {getValueFromResource(`${selectedWidget.SystemDashboardWidgetDescription}`, dispatch, t)}
                </p>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary btn-sm" onClick={() => setPreviewImage(null)}>{getValueFromResource("Close", dispatch, t)}</button>
                <button className="btn btn-primary btn-sm" onClick={handleAddWidget}>{getValueFromResource("Add", dispatch, t)}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AddWidgetsModal;